import { Select, type SelectProps } from "antd";
import type { BaseOptionType, DefaultOptionType } from "antd/es/select";
import clsx from "clsx";
import React from "react";

type SearchableOption = {
  label?: React.ReactNode;
  value?: unknown;
  children?: React.ReactNode;
};

export type AppSelectProps<
  ValueType = any,
  OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
> = SelectProps<ValueType, OptionType> & {
  searchable?: boolean;
};

const getSearchText = (value: React.ReactNode): string => {
  if (value === null || value === undefined || typeof value === "boolean") {
    return "";
  }

  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }

  if (Array.isArray(value)) {
    return value.map(getSearchText).join(" ");
  }

  if (React.isValidElement(value)) {
    return getSearchText(value.props.children);
  }

  return "";
};

export const AppSelect = Object.assign(
  <
    ValueType = any,
    OptionType extends BaseOptionType | DefaultOptionType = DefaultOptionType,
  >({
    className,
    popupClassName,
    showSearch,
    searchable = true,
    optionFilterProp = "label",
    filterOption,
    ...props
  }: AppSelectProps<ValueType, OptionType>) => {
    return (
      <Select<ValueType, OptionType>
        className={clsx("app-ant-select", className)}
        popupClassName={clsx("app-ant-select-dropdown", popupClassName)}
        showSearch={showSearch ?? searchable}
        optionFilterProp={optionFilterProp}
        filterOption={
          filterOption ??
          ((input, option) => {
            const searchableOption = option as SearchableOption | undefined;
            const optionValue = searchableOption?.value;
            const fallbackValue =
              typeof optionValue === "string" || typeof optionValue === "number"
                ? optionValue
                : "";
            const searchValue = getSearchText(
              searchableOption?.label ??
                searchableOption?.children ??
                fallbackValue,
            );

            return searchValue
              .toLowerCase()
              .includes(input.toLowerCase().trim());
          })
        }
        {...props}
      />
    );
  },
  { Option: Select.Option },
);
